import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { api, type SwitchConnectorHealth } from '@/services/api';
import { Button } from '@/components/ui/button';

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString();
}

function connectorLabel(key: SwitchConnectorHealth['connectorKey']): string {
  if (key === 'sipnav') return 'SipNav';
  if (key === 'peeredge') return 'PeerEdge';
  return 'Switch Connector';
}

export function SwitchConnectorPanel({
  isAdmin,
  isSupportAdmin,
}: {
  isAdmin: boolean;
  isSupportAdmin: boolean;
}) {
  const [health, setHealth] = useState<SwitchConnectorHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [probing, setProbing] = useState(false);
  const [generatingPrototype, setGeneratingPrototype] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      setHealth(await api.connectors.getStatus());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load connector status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const probe = async () => {
    setProbing(true);
    try {
      const result = await api.connectors.probe();
      setHealth(result);
      toast.success(`${connectorLabel(result.connectorKey)} connection verified`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Connector probe was not successful');
      await loadStatus();
    } finally {
      setProbing(false);
    }
  };

  const generateSipnavPrototype = async () => {
    setGeneratingPrototype(true);
    try {
      const result = await api.connectors.startSipnavOnboardingPrototype();
      toast.success(
        `Generated BTS review for ${result.targetUsername} with ${result.carrierCount} Carriers and ${result.accountCount} Accounts`
      );
    } catch (error) {
      toast.error(error instanceof Error
        ? error.message
        : 'The SipNav onboarding prototype could not be generated');
    } finally {
      setGeneratingPrototype(false);
    }
  };

  if (loading) {
    return (
      <section className="rounded-lg border border-surface-200 bg-white p-4 dark:border-surface-700 dark:bg-surface-900">
        <div className="flex items-center gap-2 text-sm text-surface-500 dark:text-surface-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading Switch Connector...
        </div>
      </section>
    );
  }

  const ready = health?.state === 'ready';

  return (
    <section className="rounded-lg border border-surface-200 bg-white p-4 dark:border-surface-700 dark:bg-surface-900">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h4 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
            {connectorLabel(health?.connectorKey ?? null)}
          </h4>
          <p className="mt-1 text-sm text-surface-500 dark:text-surface-400">
            {health?.message ?? 'No connector status is available.'}
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-surface-200 px-2 py-1 text-xs font-medium dark:border-surface-700">
          {ready ? (
            <CheckCircle2 className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
          ) : (
            <AlertCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
          )}
          <span className="text-surface-700 dark:text-surface-300">
            {health?.state.replace(/_/g, ' ') ?? 'unknown'}
          </span>
        </div>
      </div>

      {health && (
        <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
          <div className="rounded-md bg-surface-50 p-3 dark:bg-surface-800">
            <div className="text-xs text-surface-500 dark:text-surface-400">Last checked</div>
            <div className="mt-1 text-surface-900 dark:text-surface-100">
              {formatTimestamp(health.checkedAt)}
            </div>
          </div>
          <div className="rounded-md bg-surface-50 p-3 dark:bg-surface-800">
            <div className="text-xs text-surface-500 dark:text-surface-400">Credential scope</div>
            <div className="mt-1 text-surface-900 dark:text-surface-100">
              {health.details?.credentialScope ?? 'instance'}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => void loadStatus()} disabled={probing}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh status
        </Button>
        {isAdmin && (
          <Button size="sm" onClick={() => void probe()} disabled={probing}>
            {probing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Test connection
          </Button>
        )}
      </div>

      {health?.details?.requiresPlatformPin === true && (
        <p className="mt-3 text-xs text-amber-700 dark:text-amber-300">
          Platform {health.details.discoveredPlatformId} was discovered. Pin it in this instance's runtime configuration.
        </p>
      )}

      {isSupportAdmin && health?.connectorKey === 'sipnav' && (
        <div className="mt-4 border-t border-surface-200 pt-4 dark:border-surface-700">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
            Prototype only
          </p>
          <p className="mt-1 text-sm text-surface-500 dark:text-surface-400">
            Generate an HTML review in the admin account's Output folder from live BTS metadata.
          </p>
          <Button
            className="mt-3"
            variant="outline"
            size="sm"
            onClick={() => void generateSipnavPrototype()}
            disabled={generatingPrototype || probing || health.state !== 'ready'}
          >
            {generatingPrototype && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Generate BTS onboarding prototype
          </Button>
        </div>
      )}
    </section>
  );
}
